import { useCallback, useMemo } from 'react';
import { shallowEqual, useSelector } from 'umi';
import { v4 as uuidv4 } from 'uuid';
import {
  store,
  useLocalState,
  useAgentStoreContext,
} from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { useAgentModelsData } from '@/pages/aigc/pages/Agent/AgentCollection/hooks';
import type { IAgentData } from '@/pages/aigc/pages/Agent/AgentCollection/types';
import type { ISelectedSkillTagData } from '@/pages/aigc/pages/Agent/components/QAInput/SkillMention/SelectedSkillTag';
import {
  COPILOT_EXTERNAL_QUERY_CONFIG_KEY,
  COPILOT_NEW_SESSION_ID_KEY,
  DATABRAIN_AGENT_ID2,
} from '@/pages/aigc/pages/Agent/config/constant';

interface IHomeSelectorState {
  home?: {
    userInfo?: {
      openid?: string;
      permissions?: Record<string, unknown>;
    };
  };
}

/**
 * 外部导入的文件项（直接使用后端协议格式）：
 * - type:"sql" → SQL 查询语句 part
 * - type:"file" → 文件附件 part，按 mimeType 区分 document/image/chart
 */
export interface IExternalFileItem {
  type: 'sql' | 'file';
  text?: string;
  file?: {
    mimeType: string;
    uri: string;
  };
}

/**
 * 外部导入 Copilot 的 query / 引用前缀等配置（写入全局 store，由 Chat 读取）。
 */
export interface ICopilotExternalQueryConfig {
  /** 引用来源文案，用于输入区 chip */
  prefixTag?: string;
  /** 输入框预填全文 */
  query?: string;
  /** 来源标识，用于埋点 / metadata（如经分、首页） */
  form?: string;
  /** 游戏 code，部分来源（如自定义报表）需要透传游戏标识 */
  gameCode?: string;
  /** 报表 ID，用于「Current page」分组识别（自定义报表入口透传） */
  dashboardId?: string;
  /** 引用内容标识（由 getQuotedContent 在提交时刻注入，透传到 metadata.quoted_content） */
  quoted_content?: {
    type: string;
    value: string;
  };
  /** 外部文件附件列表（SQL / 文档 / 图片），提交时转换为对应 parts */
  files?: IExternalFileItem[];
  /** 独立图片 URL，兼容外部入参单独传递图片的场景 */
  prefixImage?: string;
  /** 外部指定的预选技能（如「AI 编辑 HTML 报告」绑定 databrain-diyreport-service），提交时透传 specified_skills */
  selectedSkills?: ISelectedSkillTagData[];
}

const SHOW_COPILOT_KEY = '__dba_show_copilot__';

/**
 * 外部打开 Copilot 的桥接 Hook。
 * 约定：本 Hook 内每个对外/内部方法实现前须有一行 JSDoc，新增方法同样遵守。
 */
export function useCopilotOpenBridge(): {
  /** 直接打开 Copilot，写入外部配置并新建会话，不重置 Agent。 */
  openCopilot: (config: ICopilotExternalQueryConfig) => boolean;
  /** 先重置为默认 DataBrain Agent，再打开 Copilot；dashboardId 用于图表「当前页」自动勾选。 */
  openCopilotWithDefaultAgent: (config: ICopilotExternalQueryConfig) => boolean;
  /**
   * 按优先级解析出第一个有权限的 Agent ID，写入 localStorage，不负责跳转。
   * 权限校验已在内部处理，外部只需传入优先级 ID 数组即可。
   * @param priorityIds - 优先级 ID 数组，按先后顺序解析出第一个有权限的 Agent ID
   */
  openCopilotWithAgentId: (priorityIds: string[]) => boolean;
  /** 仅显示 Copilot 面板，不写入外部配置、不新建会话。 */
  showCopilot: () => void;
  /** 关闭 Copilot 面板。 */
  hideCopilot: () => void;
  /** 当前用户是否具备 aigc / aigc_pc 权限。 */
  isHaveAIPermission: boolean;
} {
  const userInfo = useSelector(
    (state: IHomeSelectorState) => state.home?.userInfo,
    shallowEqual,
  );

  const permissions = useMemo((): string[] => {
    if (userInfo?.permissions) {
      return Object.keys(userInfo.permissions);
    }
    return [];
  }, [userInfo?.permissions]);

  const isHaveAIPermission = useMemo(
    () => permissions.includes('aigc') || permissions.includes('aigc_pc'),
    [permissions],
  );

  const [, setIsShowCopilot] = useLocalState<boolean>(
    SHOW_COPILOT_KEY,
    false,
  );

  // ✅ 获取 authorizedAgents 用于权限校验，handleAgentClick 用于切换 Agent（已修复跨实例同步）
  const { authorizedAgents, handleAgentClick } = useAgentModelsData();

  // 外部设置 Agent 锁（用于通知 AgentModeSelector 跳过自动重置，通过 useAgentStoreContext 同步写入）
  const { setItem: setExternalAgentLock } = useAgentStoreContext();

  /** 仅切换 Copilot 可见态为 true。 */
  const showCopilot = useCallback(() => {
    setIsShowCopilot(true);
  }, [setIsShowCopilot]);

  /** 仅切换 Copilot 可见态为 false。 */
  const hideCopilot = useCallback(() => {
    setIsShowCopilot(false);
  }, [setIsShowCopilot]);

  /** 写入外部配置、新建会话 ID、切 aiAgent 页签并打开面板。 */
  const doOpenCopilot = useCallback(
    (config: ICopilotExternalQueryConfig) => {
      const newId = uuidv4();
      store.setItem(COPILOT_EXTERNAL_QUERY_CONFIG_KEY, config);
      store.setItem(COPILOT_NEW_SESSION_ID_KEY, newId);
      store.setItem('__dba_chat_copilot__', newId);
      store.setItem('__dba_copilot_tab__', 'aiAgent');
      showCopilot();
      return true;
    },
    [showCopilot],
  );

  /** 校验 AI 权限后直接打开 Copilot，不重置 Agent。 */
  const openCopilot = useCallback(
    (config: ICopilotExternalQueryConfig) => {
      if (!isHaveAIPermission) {
        return false;
      }
      return doOpenCopilot(config);
    },
    [doOpenCopilot, isHaveAIPermission],
  );

  /** 校验 AI 权限后重置默认 Agent，再执行打开 Copilot 流程。 */
  const openCopilotWithDefaultAgent = useCallback(
    (config: ICopilotExternalQueryConfig) => {
      if (!isHaveAIPermission) {
        return false;
      }
      // ✅ 通过 handleAgentClick 写入，触发双层状态同步（useLocalStorageState + useLocalState）
      handleAgentClick(DATABRAIN_AGENT_ID2);
      const openid = userInfo?.openid;
      if (openid) {
        try {
          const agentModeMapKey = `__dba_agent_mode_map_${openid}__`;
          const agentModeMap = JSON.parse(localStorage.getItem(agentModeMapKey) || '{}');
          agentModeMap[DATABRAIN_AGENT_ID2] = 'auto';
          localStorage.setItem(agentModeMapKey, JSON.stringify(agentModeMap));
        } catch {
          // ignore
        }
      }
      return doOpenCopilot(config);
    },
    [doOpenCopilot, isHaveAIPermission, userInfo?.openid, handleAgentClick],
  );

  /**
   * 按优先级解析出第一个有权限的 Agent ID，通过 handleAgentClick 写入（触发双层状态同步），不负责跳转。
   * 权限校验已在内部处理，外部只需传入优先级 ID 数组即可。
   * 调用方需自行通过 history.push('/agent/home') 完成跳转。
   */
  const openCopilotWithAgentId = useCallback(
    (priorityIds: string[]) => {
      if (!isHaveAIPermission) {
        return false;
      }
      // 内部解析 resolvedAgentId：按优先级取第一个有权限的 agentId
      const authorizedIdSet = new Set(
        (authorizedAgents || []).map((agent: IAgentData) => String(agent.agentId)),
      );
      const found = priorityIds?.find((id) => authorizedIdSet.has(id));
      const resolvedAgentId = found || (authorizedAgents?.[0]?.agentId ?? '');
      if (resolvedAgentId) {
        // ✅ 通过 handleAgentClick 写入，触发双层状态同步（useLocalStorageState + useLocalState）
        // 通过 useAgentStoreContext 设置锁，通知 AgentModeSelector 跳过本次自动重置
        setExternalAgentLock('externalTargetAgentId', resolvedAgentId);
        handleAgentClick(resolvedAgentId);
        store.setItem('__dba_copilot_tab__', 'aiAgent');
      }
      return !!resolvedAgentId;
    },
    [isHaveAIPermission, authorizedAgents, handleAgentClick, setExternalAgentLock],
  );

  return {
    openCopilot,
    openCopilotWithDefaultAgent,
    openCopilotWithAgentId,
    showCopilot,
    hideCopilot,
    isHaveAIPermission,
  };
}
